/**
 * InboundCapacityBanner — dashboard hero-strip warning shown when ANY
 * warehouse projects > 100% capacity based on inbound container ETAs.
 *
 * Silent when everything's under threshold — no noise for the happy path.
 * When triggered, lists every over-capacity warehouse with a link to open
 * the container detail dialog so the user can consolidate.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Warning, ArrowRight } from '@phosphor-icons/react';
import { getAllInboundCapacitySummary } from '../lib/api';

export default function InboundCapacityBanner() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let alive = true;
    getAllInboundCapacitySummary(30)
      .then((r) => alive && setSummary(r.data))
      .catch(() => alive && setSummary(null));
    return () => { alive = false; };
  }, []);

  if (!summary?.any_over_capacity) return null;
  const overs = (summary.warehouses || []).filter((w) => w.warning);

  return (
    <div
      className="mb-4 border-2 border-rose-400 bg-rose-50 dark:bg-rose-950/40 p-4"
      data-testid="inbound-capacity-banner"
    >
      <div className="flex items-start gap-3">
        <Warning size={22} weight="fill" className="text-rose-600 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest font-bold text-rose-900 dark:text-rose-200">
            Inbound capacity warning · {summary.over_count} warehouse{summary.over_count === 1 ? '' : 's'}
          </div>
          <ul className="mt-2 space-y-1">
            {overs.map((w) => (
              <li key={w.warehouse_id} className="text-sm text-rose-900 dark:text-rose-100 flex items-center gap-2 flex-wrap" data-testid={`banner-wh-${w.warehouse_id}`}>
                <span className="font-mono font-bold">{w.code || w.name || w.warehouse_id.slice(0, 8)}</span>
                <span>projected</span>
                <span className="font-mono">
                  vol {w.projected_volume_pct == null ? '—' : `${w.projected_volume_pct}%`} · pallets {w.projected_pallet_pct == null ? '—' : `${w.projected_pallet_pct}%`}
                </span>
                <span className="text-rose-700 dark:text-rose-300">— {w.warning?.message}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/containers"
            className="inline-flex items-center gap-1 mt-3 text-xs font-semibold uppercase tracking-widest text-rose-900 dark:text-rose-200 hover:underline"
            data-testid="capacity-banner-cta"
          >
            Review inbound containers <ArrowRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}
